import { createTheme } from '@mui/material/styles';

// Enhanced color palette for better eye comfort
const enhancedColors = {
    primary: {
        main: '#2563eb', // Modern blue
        light: '#3b82f6',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
    },
    secondary: {
        main: '#7c3aed', // Modern purple
        light: '#8b5cf6',
        dark: '#6d28d9',
        contrastText: '#ffffff',
    },
    success: {
        main: '#059669', // Modern green
        light: '#10b981',
        dark: '#047857',
        contrastText: '#ffffff',
    },
    warning: {
        main: '#d97706', // Modern orange
        light: '#f59e0b',
        dark: '#b45309',
        contrastText: '#ffffff',
    },
    error: {
        main: '#dc2626', // Modern red
        light: '#ef4444',
        dark: '#b91c1c',
        contrastText: '#ffffff',
    },
    info: {
        main: '#0891b2', // Modern cyan
        light: '#06b6d4',
        dark: '#0e7490',
        contrastText: '#ffffff',
    },
    grey: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
    },
    background: {
        default: '#f8fafc', // Light gray background
        paper: '#ffffff',
        secondary: '#f1f5f9',
    },
    text: {
        primary: '#1e293b', // Dark gray for better readability
        secondary: '#64748b',
        disabled: '#94a3b8',
    },
    divider: '#e2e8f0',
};

// Enhanced sidebar colors
const sidebarColors = {
    background: '#1e293b', // Dark sidebar
    text: '#f1f5f9',
    active: '#3b82f6',
    hover: '#334155',
    divider: '#475569',
    icon: '#94a3b8',
    activeIcon: '#ffffff',
};

export const enhancedTheme = createTheme({
    palette: {
        mode: 'light',
        primary: enhancedColors.primary,
        secondary: enhancedColors.secondary,
        success: enhancedColors.success,
        warning: enhancedColors.warning,
        error: enhancedColors.error,
        info: enhancedColors.info,
        grey: enhancedColors.grey,
        background: enhancedColors.background,
        text: enhancedColors.text,
        divider: enhancedColors.divider,
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontWeight: 700,
            fontSize: '2.5rem',
            lineHeight: 1.2,
        },
        h2: {
            fontWeight: 600,
            fontSize: '2rem',
            lineHeight: 1.3,
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.3,
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.4,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.125rem',
            lineHeight: 1.4,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            fontWeight: 500,
            textTransform: 'none',
        },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0px 1px 2px rgba(0, 0, 0, 0.05)',
        '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
        '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
        '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
        '0px 25px 50px rgba(0, 0, 0, 0.15)',
    ],
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #e2e8f0',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
                    },
                },
                contained: {
                    '&:hover': {
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: sidebarColors.background,
                    color: sidebarColors.text,
                    borderRight: 'none',
                    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    margin: '4px 8px',
                    '&.Mui-selected': {
                        backgroundColor: sidebarColors.active,
                        color: sidebarColors.activeIcon,
                        '&:hover': {
                            backgroundColor: sidebarColors.active,
                        },
                    },
                    '&:hover': {
                        backgroundColor: sidebarColors.hover,
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                    minWidth: 40,
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontWeight: 500,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: sidebarColors.divider,
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    backgroundColor: '#e2e8f0',
                },
                bar: {
                    borderRadius: 4,
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: enhancedColors.primary.main,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid',
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    '&:before': {
                        display: 'none',
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: '#f8fafc',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 600,
                    color: '#374151',
                },
            },
        },
    },
});

export { sidebarColors };
